import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { UserPlus } from "lucide-react";
import { api, formatApiError } from "../../api";
import { OPERATIONS } from "../../constants/testIds";
import { useCelebration } from "../../contexts/CelebrationContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { toast } from "../ui/sonner";

// DEFECT-D-13/D-14: "Direct, obvious action to link members to that class" from the Class
// section — a search-as-you-type member lookup + one-click confirm, reusing the exact same
// `POST /bookings` a member's own self-serve booking uses (booking.create permission; capacity/
// waitlist/entitlement rules are all enforced identically, staff never bypasses them).
export function AddMemberToClassDialog({ open, onOpenChange, scheduleId, classLabel }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const queryClient = useQueryClient();
  const { celebrate } = useCelebration();

  useEffect(() => { if (open) { setQuery(""); setSelected(null); } }, [open]);

  const resultsQ = useQuery({
    queryKey: ["add-member-to-class-search", query],
    queryFn: async () => {
      const isPhone = /^[\d+\-\s]+$/.test(query);
      const params = isPhone ? { phone: query, limit: 8 } : { name: query, limit: 8 };
      return (await api.get("/members", { params })).data.members;
    },
    enabled: open && query.trim().length >= 2,
  });

  const bookMutation = useMutation({
    mutationFn: () => api.post("/bookings", { member_ref: selected.id, schedule_id: scheduleId }),
    onSuccess: (res) => {
      const waitlisted = res.data?.status === "waitlisted";
      if (waitlisted) toast.success(`${selected.profile.name} added to the waitlist.`);
      else celebrate("class_booked", `${selected.profile.name} added to ${classLabel}.`);
      queryClient.invalidateQueries({ queryKey: ["operations-schedules"] });
      onOpenChange(false);
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass-modal sm:rounded-[14px]" data-testid={OPERATIONS.addMemberDialog}>
        <DialogHeader>
          <DialogTitle>Add member to {classLabel}</DialogTitle>
          <DialogDescription>Search by name or phone, then confirm.</DialogDescription>
        </DialogHeader>
        <Input
          data-testid={OPERATIONS.addMemberSearchInput} placeholder="Search name or phone…"
          value={query} onChange={(e) => { setQuery(e.target.value); setSelected(null); }}
        />
        {!selected && query.trim().length >= 2 && (
          <div className="max-h-56 overflow-y-auto rounded-[10px] border" style={{ borderColor: "var(--scarlet-hairline)" }}>
            {(resultsQ.data || []).length === 0 && !resultsQ.isLoading && (
              <p className="text-sm p-3" style={{ color: "var(--scarlet-text-secondary)" }}>No members found.</p>
            )}
            {(resultsQ.data || []).map((m) => (
              <button
                key={m.id} data-testid={OPERATIONS.addMemberResultRow(m.id)} onClick={() => setSelected(m)}
                className="w-full text-left px-3 py-2 text-sm hover:bg-black/5 transition-colors flex items-center justify-between"
              >
                <span style={{ color: "var(--scarlet-text)" }}>{m.profile.name}</span>
                <span style={{ color: "var(--scarlet-text-secondary)" }}>{m.profile.phone}</span>
              </button>
            ))}
          </div>
        )}
        {selected && (
          <div className="rounded-[10px] p-3 flex items-center gap-2" style={{ background: "var(--scarlet-accent-tint)" }}>
            <UserPlus size={15} style={{ color: "var(--scarlet-accent)" }} />
            <span className="text-sm font-medium" style={{ color: "var(--scarlet-text)" }}>{selected.profile.name} · {selected.profile.phone}</span>
          </div>
        )}
        <DialogFooter>
          <Button
            data-testid={OPERATIONS.addMemberConfirmButton} disabled={!selected || bookMutation.isPending}
            onClick={() => bookMutation.mutate()} className="text-white" style={{ background: "var(--scarlet-accent)" }}
          >
            Add to class
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
